import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Container, CardMotion } from "@/components/site/Primitives";
import { SectionRail } from "@/components/site/SectionRail";
import { stockImages } from "@/lib/content";
import { loadServiceCategories } from "@/lib/content-loader";
import { LucideIconByName, resolveServiceIconName } from "@/lib/lucide/icons";
import komtarDusk from "@/assets/ksl-boardroom-bright.png.asset.json";

export const Route = createFileRoute("/services/")({
  loader: () => loadServiceCategories(),
  head: () => ({
    meta: [
      { title: "Services — Audit, Tax, Advisory, ESG & Outsourcing | KS Lau & Co" },
      { name: "description", content: "Comprehensive solutions. Practical advice. Real impact. Explore audit, tax, advisory, ESG and business outsourcing services from KS Lau & Co." },
      { property: "og:title", content: "Services — KS Lau & Co" },
      { property: "og:description", content: "Comprehensive solutions. Practical advice. Real impact. Audit, tax, advisory, ESG and outsourcing from a partner-led Penang practice." },
      { property: "og:image", content: komtarDusk.url },
      { name: "twitter:image", content: komtarDusk.url },
    ],
  }),
  component: ServicesPage,
});

function ServicesPage() {
  const serviceCategories = Route.useLoaderData();

  return (
    <>
      <SectionRail
        sections={[
          { id: "services-hero", label: "Overview" },
          { id: "services-grid", label: "Our Services" },
          { id: "services-cta", label: "Contact" },
        ]}
      />

      <section
        id="services-hero"
        className="relative isolate overflow-hidden bg-primary"
      >
        <img
          src={komtarDusk.url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-20 h-full w-full object-cover"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-r from-primary/85 via-primary/50 to-primary/10"
        />
        <Container>
          <div className="flex min-h-[32vh] max-w-3xl flex-col justify-end py-10 text-primary-foreground md:min-h-[40vh] md:py-14">
            <h1 className="text-3xl font-semibold leading-[1.15] tracking-tight md:text-4xl lg:text-5xl">
              Comprehensive Solutions. Practical Advice. Real Impact.
            </h1>
          </div>
        </Container>
      </section>

      <section id="services-grid" className="relative scroll-mt-24 bg-background pb-20 pt-16 md:pb-28 md:pt-20">
        <Container>
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-primary md:text-4xl">
              Our Range of Services
            </h2>
            <div className="mx-auto mt-4 h-[2px] w-12 bg-[#C59C6C]" />
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
              Integrated solutions designed to support your business today and prepare you for tomorrow.
            </p>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {serviceCategories.map((card, i) => (
              <CardMotion
                key={card.slug}
                animateIndex={i}
                className="group flex h-full flex-col rounded-xl bg-card p-6 shadow-[var(--shadow-card)] transition duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-soft)]"
              >
                <div className="flex items-start gap-4">
                  <span
                    aria-hidden="true"
                    className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#C59C6C]/15 text-[#C59C6C]"
                  >
                    <LucideIconByName
                      name={resolveServiceIconName(card.icon, card.slug)}
                      className="h-6 w-6"
                    />
                  </span>
                  <h3 className="text-lg font-bold leading-snug tracking-tight text-primary">
                    {card.name}
                  </h3>
                </div>

                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  {card.short}
                </p>

                <div className="mt-auto pt-6">
                  <Link
                    to="/services/$slug"
                    params={{ slug: card.slug }}
                    aria-label={`Read more about ${card.name}`}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full text-gold ring-1 ring-gold/40 transition hover:bg-gold hover:text-white"
                  >
                    <ArrowRight aria-hidden="true" className="h-4 w-4" />
                  </Link>
                </div>
              </CardMotion>
            ))}
          </div>
        </Container>
      </section>

      <section id="services-cta" className="relative scroll-mt-24 bg-background pb-24 md:pb-32">
        <Container>
          <div className="overflow-hidden rounded-2xl bg-card shadow-[var(--shadow-card)]">
            <div className="grid items-stretch gap-0 md:grid-cols-2">
              <div className="flex flex-col justify-center p-10 md:p-14">
                <h3 className="text-3xl font-bold leading-tight tracking-tight text-primary md:text-4xl">
                  Let's build your next chapter together.
                </h3>
                <div className="mt-5 h-[2px] w-12 bg-[#C59C6C]" />
                <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground md:text-lg">
                  Whether you're a growing business or an established enterprise, we're here to help
                  you navigate challenges and seize opportunities with confidence.
                </p>
                <div className="mt-7">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-gold bg-transparent px-7 py-3 text-base font-semibold text-gold transition hover:bg-gold/10"
                  >
                    Speak to Our Team <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>

              <div className="relative min-h-[280px] bg-primary/5 md:min-h-full">
                <img
                  src={stockImages.brandDesk}
                  alt="KS Lau & Co branded mug and notebook on a desk"
                  loading="lazy"
                  width={1600}
                  height={1100}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
